import { get, post } from "./api";
import { clearAuthToken } from "../common/utils/tokenStorage";
import { clearSelectedRestaurant } from "../common/utils/restaurantContext";
import { cacheCsrfToken, clearCsrfTokenCache } from "../common/utils/csrf";
import { getCurrentUser as getCachedCurrentUser, setCurrentUser } from "../common/utils/jwt";

type UserPayload = {
  id?: number;
  role?: string;
  [key: string]: unknown;
};

type LoginResponse = {
  token?: string;
  csrfToken?: string;
  user?: UserPayload;
};

export async function login(email: string, password: string) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const response = await post<LoginResponse>('/api/auth/login', { email, password }, { signal: controller.signal });
    clearSelectedRestaurant();
    clearAuthToken();
    clearCsrfTokenCache();
    if (response.csrfToken) cacheCsrfToken(response.csrfToken);
    if (response.user) setCurrentUser(response.user);
    return response;
  } catch (error) {
    if (controller.signal.aborted) throw new Error('Sign in timed out. Please check your connection and try again.');
    throw error;
  } finally { clearTimeout(timeout); }
}

let pendingLogout: Promise<void> | null = null;
export function logout(): Promise<void> {
  if (pendingLogout) return pendingLogout;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12000);
  pendingLogout = (async () => {
    try {
      await post('/api/auth/logout', {}, { signal: controller.signal });
      clearAuthToken();
      clearSelectedRestaurant();
      clearCsrfTokenCache();
    } catch (error) {
      if (controller.signal.aborted) throw new Error('Sign out timed out. Please check your connection and try again.');
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  })().finally(() => { pendingLogout = null; });
  return pendingLogout;
}

export async function getCurrentUser() {
  const response = await get<{ user?: UserPayload }>("/api/auth/me");
  const user = response?.user;
  if (!user) {
    throw new Error("Unable to determine current user");
  }
  setCurrentUser(user);
  return user;
}

export function isAuthenticated() {
  return Boolean(getCachedCurrentUser());
}

export async function forgotPassword(email: string): Promise<void> {
  await post("/api/auth/forgot-password", { email });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await post("/api/auth/reset-password", { token, password });
}
